// The SMS consent confirmation copy. This is a versioned legal text, not UI strings, and this file
// is the ONE copy of it in the app. The API (confirmMarketingConsent, TRK-5807) records only that
// the practice confirmed, and who and when; it serves no wording. So a change here means bumping
// SMS_CONSENT_VERSION and getting counsel's sign-off again, and it cannot be tied to confirmations
// already recorded.
//
// `{workspaceName}` is filled in by the modal. `**` marks bold.

export const SMS_CONSENT_VERSION = `sms-consent-v1`

export type SmsConsentStatement = { key: string; title: string; body: string }

export type SmsConsentText = {
  heading: string
  intro: string
  confirmPrompt: string
  statements: SmsConsentStatement[]
  disclaimer: string
  examplesTitle: string
  examples: string[]
  agreeLabel: string
}

export const SMS_CONSENT_TEXT: SmsConsentText = {
  heading: `Before you turn on texting`,
  intro: `Federal and state law require your practice to have a person's permission **before** texting them. Trackable sends these messages for you, but it can't see or check your patients' consent. That part is yours.`,
  confirmPrompt: `Please confirm each of the following for **{workspaceName}**:`,
  statements: [
    {
      key: `has_consent`,
      title: `We have consent.`,
      body: `Everyone this workspace texts through Trackable has agreed to receive that kind of message from our practice. For marketing or promotional texts, that agreement is in writing.`,
    },
    {
      key: `verified`,
      title: `We've checked it.`,
      body: `For contacts we imported or received from a lead source, we've confirmed the consent is real, covers the number being texted, and hasn't been withdrawn.`,
    },
    {
      key: `keep_proof`,
      title: `We keep proof.`,
      body: `We keep a record of each person's consent and can provide it to Trackable if asked.`,
    },
    {
      key: `honor_opt_outs`,
      title: `We honor opt-outs.`,
      body: `When someone replies STOP or asks us in any other way to stop texting, we stop, and we don't text them again unless they give new consent.`,
    },
    {
      key: `our_responsibility`,
      title: `This is our responsibility.`,
      body: `I understand that our practice, not Trackable, is responsible for texts sent without proper consent, including any resulting claims, fines, or carrier penalties, as described in our agreement with Trackable. I'm authorized to confirm this on behalf of {workspaceName}.`,
    },
  ],
  disclaimer: `This isn't legal advice. If you're unsure whether your consent is sufficient, talk to your attorney before turning on texting.`,
  examplesTitle: `What counts as consent?`,
  examples: [
    `A signed intake form that says the patient agrees to receive texts from your practice counts.`,
    `A web form with an opt-in box for texts that starts unchecked, which the person ticked themselves, counts.`,
    `A phone number on a purchased lead list is **not** consent, no matter what the seller says.`,
  ],
  agreeLabel: `Agree and turn on texting`,
}
